import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import API from '../config/api';
import IMAGES from '../config/images';
import { useLanguage, useDynamicTranslation, PriceTag } from '../context/LanguageContext';

export default function Shop() {
  const { lang, t, filterLabel } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const activeCategory = searchParams.get('category') || '';
  const activeSort = searchParams.get('sort') || 'newest';
  const searchQuery = searchParams.get('search') || searchParams.get('q') || '';
  
  const activeSizes = searchParams.get('sizes') || '';
  const activeColors = searchParams.get('colors') || '';
  const activeRooms = searchParams.get('rooms') || '';
  const activeMaterials = searchParams.get('materials') || '';
  const activeConstructions = searchParams.get('constructions') || '';
  const activeShapes = searchParams.get('shapes') || '';

  useEffect(() => {
    fetch(API.products.categories)
      .then(res => res.json())
      .then(data => { if (data.status === 'success') setCategories(data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory) params.append('category_id', activeCategory);
    if (activeSort) params.append('sort', activeSort);
    if (searchQuery) params.append('search', searchQuery);
    if (activeSizes) params.append('sizes', activeSizes);
    if (activeColors) params.append('colors', activeColors);
    if (activeRooms) params.append('rooms', activeRooms);
    if (activeMaterials) params.append('materials', activeMaterials);
    if (activeConstructions) params.append('constructions', activeConstructions);
    if (activeShapes) params.append('shapes', activeShapes);
    params.append('limit', '40');

    fetch(`${API.products.list}?${params.toString()}`)
      .then(res => res.json())
      .then(data => { if (data.status === 'success') { setProducts(data.data); setTotal(data.total || data.data.length); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCategory, activeSort, searchQuery, activeSizes, activeColors, activeRooms, activeMaterials, activeConstructions, activeShapes]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    value ? newParams.set(key, value) : newParams.delete(key);
    setSearchParams(newParams);
  };

  const activeCatName = activeCategory ? categories.find(c => c.id == activeCategory)?.name : null;
  const translatedCatName = useDynamicTranslation(activeCatName);

  return (
    <div className="shop-page">
      {/* ─── Breadcrumb + Title ─── */}
      <div className="shop-header">
        <div className="shop-breadcrumb">
          <Link to="/">{lang === 'de' ? t('shop.home') : 'Home'}</Link> <span>/</span> <Link to="/shop">{lang === 'de' ? t('shop.rugs') : 'Rugs'}</Link>
          {activeCatName && <><span>/</span> <span className="bc-active">{translatedCatName || activeCatName}</span></>}
        </div>
        <h1 className="shop-title">{activeCatName ? (translatedCatName || activeCatName) : (lang === 'de' ? t('shop.title') : 'All Rugs')}</h1>
        <p className="shop-subtitle">
          {lang === 'de' ? t('shop.subtitle') : 'Discover our stunning collection of handmade rugs. From the latest carpet designs to unique patterns, we offer a variety of styles to suit any space.'}
        </p>
      </div>

      {/* ─── Mobile Filter Toggle ─── */}
      <button className="shop-mobile-filter-btn" onClick={() => setMobileFilterOpen(!mobileFilterOpen)}>
        ☰ {lang === 'de' ? t('shop.mobileFilter') : 'Filters'} {activeCategory ? '(1)' : ''}
      </button>

      {/* ─── Toolbar ─── */}
      <div className="shop-toolbar">
        <span className="shop-count">{loading ? (lang === 'de' ? t('shop.loading') : 'Loading...') : `${products.length} ${products.length === 1 ? (lang === 'de' ? t('shop.product') : 'product') : (lang === 'de' ? t('shop.products') : 'products')}`}</span>
        <div className="shop-sort-wrapper">
          <span className="shop-sort-label">{lang === 'de' ? t('shop.sortBy') : 'SORT BY'}</span>
          <select className="shop-sort" value={activeSort} onChange={e => updateFilter('sort', e.target.value)}>
            <option value="newest">{lang === 'de' ? t('shop.sortNewest') : 'Newest'}</option>
            <option value="price_low">{lang === 'de' ? t('shop.sortPriceLow') : 'Price: Low to High'}</option>
            <option value="price_high">{lang === 'de' ? t('shop.sortPriceHigh') : 'Price: High to Low'}</option>
            <option value="name">{lang === 'de' ? t('shop.sortName') : 'Name: A–Z'}</option>
          </select>
        </div>
      </div>

      {/* ─── Main Content ─── */}
      <div className="shop-layout">
        
        {/* ─── Sidebar ─── */}
        <aside className={`shop-sidebar ${mobileFilterOpen ? 'open' : ''}`}>
          <div className="sidebar-section">
            <h4 className="sidebar-heading">{lang === 'de' ? t('shop.filterBy') : 'FILTER BY'}</h4>
          </div>

          {/* Active Filters */}
          {(activeCategory || activeSizes || activeColors || activeRooms || activeMaterials || activeConstructions || activeShapes) && (
            <div className="sidebar-active-filters">
              {activeCategory && <span className="active-tag">{translatedCatName || activeCatName} <button onClick={() => updateFilter('category', '')} className="tag-remove">×</button></span>}
              {activeSizes && <span className="active-tag">{filterLabel('sizes', activeSizes)} <button onClick={() => updateFilter('sizes', '')} className="tag-remove">×</button></span>}
              {activeColors && <span className="active-tag">{filterLabel('colors', activeColors)} <button onClick={() => updateFilter('colors', '')} className="tag-remove">×</button></span>}
              {activeRooms && <span className="active-tag">{filterLabel('rooms', activeRooms)} <button onClick={() => updateFilter('rooms', '')} className="tag-remove">×</button></span>}
              {activeMaterials && <span className="active-tag">{filterLabel('materials', activeMaterials)} <button onClick={() => updateFilter('materials', '')} className="tag-remove">×</button></span>}
              {activeConstructions && <span className="active-tag">{filterLabel('constructions', activeConstructions)} <button onClick={() => updateFilter('constructions', '')} className="tag-remove">×</button></span>}
              {activeShapes && <span className="active-tag">{filterLabel('shapes', activeShapes)} <button onClick={() => updateFilter('shapes', '')} className="tag-remove">×</button></span>}
            </div>
          )}

          {/* Category */}
          <div className="sidebar-section">
            <h4 className="sidebar-heading">{lang === 'de' ? t('shop.filters.category') : 'CATEGORY'}</h4>
            <ul className="sidebar-list">
              <li className={`sidebar-item ${!activeCategory ? 'active' : ''}`} onClick={() => updateFilter('category', '')}>
                <span className="sidebar-checkbox">{!activeCategory ? '☑' : '☐'}</span> {lang === 'de' ? t('shop.filters.allRugs') : 'All Rugs'} <span className="sidebar-count">({total})</span>
              </li>
              {categories.map(cat => (
                <li key={cat.id} className={`sidebar-item ${activeCategory == cat.id ? 'active' : ''}`} onClick={() => updateFilter('category', cat.id)}>
                  <span className="sidebar-checkbox">{activeCategory == cat.id ? '☑' : '☐'}</span> <CategoryName name={cat.name} />
                </li>
              ))}
            </ul>
          </div>

          {/* Filters Generator */}
          {[
            { key: 'sizes', title: lang === 'de' ? t('shop.filters.size') : 'SIZE', options: ['2x3 FT', '3x5 FT', '4x6 FT', '5x8 FT', '6x9 FT', '8x10 FT', '9x12 FT', '10x14 FT', '12x15 FT', 'Small', 'Medium', 'Large', 'Oversize'], active: activeSizes },
            { key: 'colors', title: lang === 'de' ? t('shop.filters.color') : 'COLOR', options: ['Blue', 'Red', 'Green', 'Yellow', 'Ivory / White', 'Grey', 'Black', 'Orange', 'Pink', 'Purple', 'Brown', 'Beige', 'Multi Color'], active: activeColors },
            { key: 'rooms', title: lang === 'de' ? t('shop.filters.room') : 'ROOM', options: ['Living Room', 'Dining Room', 'Bedroom', 'Kids Room', 'Outdoor/Indoor'], active: activeRooms },
            { key: 'shapes', title: lang === 'de' ? t('shop.filters.shape') : 'SHAPE', options: ['Rectangle', 'Irregular', 'Round', 'Runner', 'Oval', 'Square'], active: activeShapes },
            { key: 'materials', title: lang === 'de' ? t('shop.filters.material') : 'MATERIAL', options: ['Wool', 'Wool & Bamboo Silk', 'Wool & Silk', 'Silk', 'Viscose', 'Jute & Hemp', 'Cotton', 'Polyester'], active: activeMaterials },
            { key: 'constructions', title: lang === 'de' ? t('shop.filters.construction') : 'CONSTRUCTION', options: ['Hand Knotted', 'Hand Tufted', 'Hand Loom', 'Flat Weaves', 'Shag'], active: activeConstructions },
          ].map(filter => (
            <div key={filter.key} className="sidebar-section">
              <h4 className="sidebar-heading">{filter.title}</h4>
              <ul className="sidebar-list">
                {filter.options.map(opt => (
                  <li key={opt} className={`sidebar-item ${filter.active === opt ? 'active' : ''}`} onClick={() => updateFilter(filter.key, filter.active === opt ? '' : opt)}>
                    <span className="sidebar-checkbox">{filter.active === opt ? '☑' : '☐'}</span> {filterLabel(filter.key, opt)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>

        {/* ─── Products Grid ─── */}
        <main className="shop-products">
          {loading ? (
            <div className="shop-empty">
              <div className="shop-loader"></div>
              <p>{lang === 'de' ? t('shop.loading') : 'Loading collection...'}</p>
            </div>
          ) : products.length > 0 ? (
            <div className="shop-grid">
              {products.map(p => (
                <ShopProductCard key={p.id} product={p} />
              ))}
            </div>
          ) : (
            <div className="shop-empty">
              <p className="shop-empty-title">{lang === 'de' ? t('shop.noProducts') : 'No products found'}</p>
              <p className="shop-empty-sub">{lang === 'de' ? t('shop.noProductsSub') : 'Products will appear here once added from the Admin Panel.'}</p>
            </div>
          )}
        </main>
      </div>

      <style>{shopStyles}</style>
    </div>
  );
}


/* ─── Dynamic Category Name Component ─── */
function CategoryName({ name }) {
  const translated = useDynamicTranslation(name);
  return <>{translated || name}</>;
}

/* ─── Small Product Card (Heritage Rug Style) ─── */
function ShopProductCard({ product }) {
  const { lang, t } = useLanguage();
  const { id, sku, name, b2c_price, image_url, category_name, size_variants, category_discount_percent } = product;
  const imgSrc = image_url || IMAGES.products[((id || 1) - 1) % IMAGES.products.length];
  const [isHovered, setIsHovered] = useState(false);
  const translatedName = useDynamicTranslation(name);
  const translatedCategory = useDynamicTranslation(category_name);

  // Parse size_variants and find the smallest size's B2C price
  let displayPrice = b2c_price || 0;
  let hasVariantPrice = false;
  try {
    const variants = typeof size_variants === 'string' ? JSON.parse(size_variants || '[]') : (size_variants || []);
    if (variants.length > 0) {
      // Sort by B2C price ascending, pick the cheapest
      const sorted = [...variants].filter(v => v.b2c && Number(v.b2c) > 0).sort((a, b) => Number(a.b2c) - Number(b.b2c));
      if (sorted.length > 0) {
        displayPrice = sorted[0].b2c;
        hasVariantPrice = true;
      }
    }
  } catch (e) {}

  const discountPercentRaw = Number(category_discount_percent || 0);
  const discountPercent = Number.isFinite(discountPercentRaw) ? Math.max(0, Math.min(95, discountPercentRaw)) : 0;
  const discountedPrice = discountPercent > 0
    ? Math.round((Number(displayPrice) * (1 - (discountPercent / 100))) * 100) / 100
    : Number(displayPrice);

  return (
    <Link 
      to={`/product/${sku || id}`}  
      className="spc" 
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="spc-img-wrap">
        {/* "New" Badge */}
        <span className="spc-badge">{lang === 'de' ? t('shop.newBadge') : 'New'}</span>
        
        {/* Wishlist */}
        <button className="spc-wishlist" onClick={e => { e.preventDefault(); }}>♡</button>
        
        {/* Image */}
        <div className="spc-img" style={{ 
          backgroundImage: `url("${imgSrc}")`, 
          transform: isHovered ? 'scale(1.05)' : 'scale(1)' 
        }} />
      </div>

      <div className="spc-info">
        <p className="spc-name">{translatedName || name}</p>
        <p className="spc-meta">{translatedCategory || category_name || (lang === 'de' ? t('shop.handcrafted') : 'Handcrafted')}</p>
        <div className="spc-prices">
          {discountPercent > 0 && <span className="spc-old-price">{hasVariantPrice ? (lang === 'de' ? t('shop.from') + ' ' : 'From ') : ''} <PriceTag inr={displayPrice} /></span>}
          <span className="spc-price">{hasVariantPrice ? (lang === 'de' ? t('shop.from') + ' ' : 'From ') : ''} <PriceTag inr={discountedPrice} /></span>
          {discountPercent > 0 && <span className="spc-discount-pill">{discountPercent}% {lang === 'de' ? 'RABATT' : 'OFF'}</span>}
        </div>
      </div>
    </Link>
  );
}


/* ─── Shop Styles ─── */
const shopStyles = `
/* ═══ Page Layout ═══ */
.shop-page { background: #fff; min-height: 100vh; }

.shop-header {
  text-align: center;
  padding: 45px 20px 30px;
  border-bottom: 1px solid #eee;
}
.shop-breadcrumb {
  font-size: 12px;
  color: #999;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
}
.shop-breadcrumb a { color: #999; text-decoration: none; }
.shop-breadcrumb a:hover { color: #333; text-decoration: underline; }
.shop-breadcrumb span { margin: 0 6px; }
.shop-breadcrumb .bc-active { color: #333; }
.shop-title {
  font-family: var(--font-serif, Georgia, serif);
  font-size: 2.4rem;
  font-weight: 400;
  color: #1a1a1a;
  margin: 0 0 12px;
}
.shop-subtitle {
  font-size: 13.5px;
  color: #888;
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.7;
}

/* ═══ Toolbar ═══ */
.shop-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 40px 12px 290px;
  border-bottom: 1px solid #eee;
  font-size: 12.5px;
}
.shop-count { color: #999; letter-spacing: 0.3px; }
.shop-sort-wrapper { display: flex; align-items: center; gap: 10px; }
.shop-sort-label { color: #aaa; font-size: 11px; letter-spacing: 1.5px; font-weight: 500; }
.shop-sort {
  padding: 7px 28px 7px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  font-size: 12.5px;
  font-family: inherit;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23999'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

/* ═══ Layout (Sidebar + Grid) ═══ */
.shop-layout { display: flex; min-height: 70vh; }

/* ═══ Sidebar ═══ */
.shop-sidebar {
  width: 250px;
  flex-shrink: 0;
  padding: 28px 24px;
  border-right: 1px solid #eee;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  position: sticky;
  top: 80px;
}
.sidebar-section { margin-bottom: 28px; }
.sidebar-heading {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  color: #333;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.sidebar-list { list-style: none; padding: 0; margin: 0; }
.sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  font-size: 13.5px;
  color: #555;
  cursor: pointer;
  transition: color 0.2s;
}
.sidebar-item:hover { color: #111; }
.sidebar-item.active { color: #111; font-weight: 500; }
.sidebar-checkbox { font-size: 14px; color: #bbb; }
.sidebar-item.active .sidebar-checkbox { color: #333; }
.sidebar-count { font-size: 11px; color: #bbb; margin-left: auto; }
.sidebar-more { font-size: 12px; color: var(--accent-gold, #b8860b); cursor: pointer; padding: 8px 0 0; font-weight: 500; }
.sidebar-more:hover { text-decoration: underline; }

.sidebar-active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}
.active-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #f5f5f5;
  border-radius: 3px;
  font-size: 12px;
  color: #333;
}
.tag-remove {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #999;
  padding: 0;
  line-height: 1;
}
.tag-remove:hover { color: #333; }

/* ═══ Products Grid ═══ */
.shop-products { flex: 1; padding: 28px 32px; }
.shop-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

/* ═══ Product Card ═══ */
.spc {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
}
.spc:hover { opacity: 0.95; }
.spc-img-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  overflow: hidden;
  background: #f7f5f2;
  margin-bottom: 12px;
}
.spc-img {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
}
.spc-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #c9553d;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  padding: 3px 10px;
  z-index: 2;
  text-transform: uppercase;
}
.spc-wishlist {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  z-index: 2;
  color: #999;
  transition: color 0.2s;
}
.spc-wishlist:hover { color: #c9553d; }

.spc-info { padding: 0 2px; }
.spc-name {
  font-size: 13px;
  font-weight: 500;
  color: #222;
  margin: 0 0 3px;
  line-height: 1.4;
}
.spc-meta {
  font-size: 11px;
  color: #aaa;
  margin: 0 0 6px;
  letter-spacing: 0.3px;
}
.spc-prices { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.spc-old-price {
  font-size: 12px;
  color: #9ca3af;
  text-decoration: line-through;
}
.spc-price {
  font-size: 13.5px;
  font-weight: 500;
  color: #222;
}
.spc-discount-pill {
  font-size: 10px;
  font-weight: 700;
  color: #b42318;
  background: #ffe9e7;
  border: 1px solid #f8c9c4;
  border-radius: 999px;
  padding: 2px 7px;
}

/* ═══ Empty / Loading States ═══ */
.shop-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  color: #999;
}
.shop-empty-title { font-size: 1.1rem; margin-bottom: 6px; }
.shop-empty-sub { font-size: 13px; }
.shop-loader {
  width: 30px; height: 30px;
  border: 2.5px solid #eee;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 14px;
}
@keyframes spin { to { transform: rotate(360deg); }}

/* ═══ Mobile Filter ═══ */
.shop-mobile-filter-btn {
  display: none;
  width: 100%;
  padding: 12px;
  background: #fafafa;
  border: none;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 1px;
  cursor: pointer;
  color: #333;
}

/* ═══ Responsive ═══ */
@media (max-width: 1024px) {
  .shop-grid { grid-template-columns: repeat(3, 1fr); }
  .shop-toolbar { padding-left: 270px; }
}
@media (max-width: 768px) {
  .shop-mobile-filter-btn { display: block; }
  .shop-toolbar { padding: 12px 16px; }
  .shop-title { font-size: 1.8rem; }
  .shop-subtitle { font-size: 12.5px; }
  .shop-sidebar {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: 280px;
    z-index: 999;
    background: #fff;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    box-shadow: 2px 0 12px rgba(0,0,0,0.1);
    max-height: 100vh;
  }
  .shop-sidebar.open { transform: translateX(0); }
  .shop-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
  .shop-products { padding: 16px; }
}
@media (max-width: 480px) {
  .shop-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .spc-name { font-size: 12px; }
  .spc-price { font-size: 12px; }
}
`;
